import {useCallback, useEffect, useMemo, useState} from "react";
import {isAuthenticated} from "../apicalls/auth";
import {
  getDeferredPayouts,
  getRestringingOrders,
  retryRestringingTransfers,
} from "../apicalls/restringingVendors";
import {API} from "../../constAPI";
import "./style/RestringingVendors.css";

const formatMoney = (cents) =>
  new Intl.NumberFormat("en-US", {style: "currency", currency: "USD"}).format(
    Number(cents || 0) / 100
  );

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

export const isTestStripeDashboardApi = (apiUrl = API) => {
  if (!apiUrl) return true;
  try {
    const hostname = new URL(apiUrl).hostname.toLowerCase();
    return hostname === "localhost"
      || hostname === "127.0.0.1"
      || hostname.includes("staging")
      || hostname.includes("test")
      || hostname.includes("dev");
  } catch (_) {
    return true;
  }
};

export const stripePaymentDashboardUrl = (paymentIntentId, apiUrl = API) => {
  const id = String(paymentIntentId || "").trim();
  if (!id) return null;
  const modePath = isTestStripeDashboardApi(apiUrl) ? "/test" : "";
  return `https://dashboard.stripe.com${modePath}/payments/${encodeURIComponent(id)}`;
};

const PAYOUT_READY_STATUSES = new Set(["picked_up", "fulfilled"]);

export const needsCommissionTransferRetry = (order = {}) => {
  if (order.payment_status !== "paid") return false;
  if (!PAYOUT_READY_STATUSES.has(order.fulfillment_status)) return false;
  if (!order.stripe_payment_intent_id) return false;
  const vendorTransferRequired = order.vendor_transfer_required !== false;
  const coachTransferRequired = order.coach_transfer_required !== undefined
    ? Boolean(order.coach_transfer_required)
    : Number(order.coach_commission_cents || 0) > 0;
  if (vendorTransferRequired && !order.vendor_transfer_id) return true;
  return coachTransferRequired && !order.coach_transfer_id;
};

export const commissionTransferStatus = (order = {}) => {
  if (order.payment_status === "paid" && !PAYOUT_READY_STATUSES.has(order.fulfillment_status)) {
    return {
      vendor: "Scheduled after pickup",
      coach: "Scheduled after pickup",
      label: "Deferred",
    };
  }
  if (order.payment_status === "paid" && !order.stripe_payment_intent_id) {
    return {
      vendor: "Not Stripe-paid",
      coach: "None",
      label: "Not applicable",
    };
  }
  const coachCommissionCents = Number(order.coach_commission_cents || 0);
  const vendorTransferRequired = order.vendor_transfer_required !== false;
  const coachTransferRequired = order.coach_transfer_required !== undefined
    ? Boolean(order.coach_transfer_required)
    : coachCommissionCents > 0;
  const vendor = !vendorTransferRequired
    ? "Not applicable"
    : order.vendor_transfer_id
      ? "Settled"
      : "Missing";
  const coach = !coachTransferRequired
    ? "None"
    : order.coach_transfer_id
      ? "Settled"
      : "Pending";

  return {
    vendor,
    coach,
    label: needsCommissionTransferRetry(order) ? "Needs retry" : "Settled",
  };
};

export const payoutRemaining = (orders = []) => orders.reduce((summary, order) => {
  if (order.payment_status !== "paid" || order.fulfillment_status !== "pending") return summary;
  const total = Number(order.total_cents || 0);
  const platformFee = Number(order.platform_fee_cents || 0);
  const coachCommission = Number(order.coach_commission_cents || 0);
  const vendorPayout = Math.max(0, total - platformFee - coachCommission);
  return { orders: summary.orders + 1, cents: summary.cents + vendorPayout + Math.max(0, coachCommission) };
}, { orders: 0, cents: 0 });

export const lessonPayoutRemaining = (payouts = []) => payouts.reduce((summary, payout) => {
  if (payout.entity_type !== "lesson" || payout.status !== "pending") return summary;
  return {
    payouts: summary.payouts + 1,
    cents: summary.cents + Math.max(0, Number(payout.amount_cents || 0)),
  };
}, { payouts: 0, cents: 0 });

export const lessonPayoutStatus = (payout = {}) => {
  if (payout.status === "transferred") return "Transferred";
  if (payout.status === "cancelled") return "Cancelled";
  if (payout.status === "pending" && payout.eligible_at) return "Scheduled";
  if (payout.status === "pending") return "Waiting";
  return payout.status || "-";
};

export const formatRetryResult = (result = {}) => {
  if (result.detail) return result.detail;
  const failures = (result.failures || [])
    .map((failure) => `#${failure.order_id}: ${failure.error}`)
    .join(", ");
  return [
    `Attempted ${Number(result.attempted || 0)}`,
    `settled ${Number(result.settled || 0)}`,
    `pending ${Number(result.pending || 0)}`,
    `failed ${Number(result.failed || 0)}`,
    failures,
  ].filter(Boolean).join(" · ");
};

function CoachCommissions() {
  const user = isAuthenticated();
  const accessToken = user?.access_token;
  const [orders, setOrders] = useState([]);
  const [lessonPayouts, setLessonPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [retryingOrderId, setRetryingOrderId] = useState("");
  const [manualOrderId, setManualOrderId] = useState("");

  const loadOrders = useCallback(() => {
    if (!accessToken) return Promise.resolve();
    setLoading(true);
    setError("");
    return Promise.all([
      getRestringingOrders(accessToken, {payment_status: "paid"}),
      getDeferredPayouts(accessToken, {entity_type: "lesson", status: "pending"}),
    ])
      .then(([orderData, payoutData]) => {
        if (orderData?.detail || payoutData?.detail) {
          setError(orderData?.detail || payoutData?.detail);
          setOrders([]);
          setLessonPayouts([]);
          return;
        }
        setOrders(orderData?.orders || []);
        setLessonPayouts(payoutData?.payouts || []);
      })
      .catch((err) => {
        setError(err.message || "Unable to load coach commissions.");
        setOrders([]);
        setLessonPayouts([]);
      })
      .finally(() => setLoading(false));
  }, [accessToken]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const pendingOrders = useMemo(
    () => orders.filter(needsCommissionTransferRetry),
    [orders]
  );

  const settledOrders = useMemo(
    () => orders.filter((order) => Number(order.coach_commission_cents || 0) > 0 && !needsCommissionTransferRetry(order)),
    [orders]
  );

  const remainingPayout = useMemo(() => payoutRemaining(orders), [orders]);
  const remainingLessonPayout = useMemo(() => lessonPayoutRemaining(lessonPayouts), [lessonPayouts]);

  const retryOrder = async (orderId) => {
    if (!orderId || !accessToken) return;
    setRetryingOrderId(String(orderId));
    setError("");
    setMessage("");
    try {
      const result = await retryRestringingTransfers({orderId, limit: 1}, accessToken);
      const formatted = formatRetryResult(result);
      if (result?.detail || Number(result?.failed || 0) > 0) {
        setError(formatted);
      } else {
        setMessage(formatted);
      }
      await loadOrders();
    } catch (err) {
      setError(err.message || "Transfer retry failed.");
    } finally {
      setRetryingOrderId("");
    }
  };

  const retryManualOrder = (event) => {
    event.preventDefault();
    retryOrder(manualOrderId);
  };

  return (
    <main className="restringing-vendors">
      <div className="restringing-vendors__head">
        <div>
          <h1>Coach commissions</h1>
          <p>Diagnose and retry restringing commission transfers for paid coach-created orders.</p>
        </div>
      </div>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Commerce payout remaining</h2>
          <span>{remainingPayout.orders} order{remainingPayout.orders === 1 ? "" : "s"}</span>
        </div>
        <strong>{formatMoney(remainingPayout.cents)}</strong>
        <p>Paid customer funds held by The Tennis Plan. Vendor and coach payouts release only after each order is picked up.</p>
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Deferred lesson payouts</h2>
          <span>{remainingLessonPayout.payouts} payout{remainingLessonPayout.payouts === 1 ? "" : "s"}</span>
        </div>
        <strong>{formatMoney(remainingLessonPayout.cents)}</strong>
        <p>Stripe-paid lesson payouts for all lesson types release after the lesson end plus the payout delay.</p>
        {loading ? (
          <p>Loading lesson payouts...</p>
        ) : lessonPayouts.length === 0 ? (
          <p>No deferred lesson payouts found.</p>
        ) : (
          <LessonPayoutTable payouts={lessonPayouts} />
        )}
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Retry a transfer</h2>
          <span>{pendingOrders.length} need attention</span>
        </div>
        <form className="restringing-inline-form" onSubmit={retryManualOrder}>
          <label>
            Order ID
            <input
              type="number"
              min="1"
              value={manualOrderId}
              onChange={(event) => setManualOrderId(event.target.value)}
              placeholder="98"
            />
          </label>
          <button type="submit" disabled={!manualOrderId || Boolean(retryingOrderId)}>
            {retryingOrderId === String(manualOrderId) ? "Retrying..." : "Retry order transfer"}
          </button>
        </form>
        {error && <div className="restringing-alert restringing-alert--error">{error}</div>}
        {message && <div className="restringing-alert restringing-alert--success">{message}</div>}
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Needs attention</h2>
          <span>{pendingOrders.length} paid orders missing transfers</span>
        </div>
        {loading ? (
          <p>Loading commissions...</p>
        ) : pendingOrders.length === 0 ? (
          <p>No paid coach commissions are missing transfers.</p>
        ) : (
          <CommissionTable
            orders={pendingOrders}
            retryingOrderId={retryingOrderId}
            onRetry={retryOrder}
          />
        )}
      </section>

      <section className="restringing-card">
        <div className="restringing-section-head">
          <h2>Recently settled</h2>
          <span>{settledOrders.length} paid coach commissions settled</span>
        </div>
        {settledOrders.length === 0 ? (
          <p>No settled coach commissions found in the current paid-order load.</p>
        ) : (
          <CommissionTable orders={settledOrders.slice(0, 25)} />
        )}
      </section>
    </main>
  );
}

function LessonPayoutTable({payouts}) {
  return (
    <div className="restringing-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Payout</th>
            <th>Lesson</th>
            <th>Type</th>
            <th>Coach</th>
            <th>Player</th>
            <th>Amount</th>
            <th>Lesson end</th>
            <th>Eligible</th>
            <th>PaymentIntent</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((payout) => {
            const stripeUrl = stripePaymentDashboardUrl(payout.payment_intent_id);
            return (
              <tr key={payout.id}>
                <td>#{payout.id}</td>
                <td>#{payout.entity_id}</td>
                <td>{payout.lesson_type_name || payout.lessontype_id || "Lesson"}</td>
                <td>{payout.coach_name || "-"}</td>
                <td>{payout.player_name || "-"}</td>
                <td>{formatMoney(payout.amount_cents)}</td>
                <td>{formatDate(payout.end_date_time_tz || payout.end_date_time)}</td>
                <td>{formatDate(payout.eligible_at)}</td>
                <td>
                  {stripeUrl ? (
                    <a href={stripeUrl} target="_blank" rel="noreferrer">{payout.payment_intent_id}</a>
                  ) : "-"}
                </td>
                <td><span className="restringing-pill">{lessonPayoutStatus(payout)}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CommissionTable({orders, retryingOrderId = "", onRetry = null}) {
  return (
    <div className="restringing-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Coach</th>
            <th>Vendor</th>
            <th>Commission</th>
            <th>Vendor transfer</th>
            <th>Coach transfer</th>
            <th>PaymentIntent</th>
            <th>Paid</th>
            <th>Status</th>
            {onRetry && <th></th>}
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const status = commissionTransferStatus(order);
            const stripeUrl = stripePaymentDashboardUrl(order.stripe_payment_intent_id);
            return (
              <tr key={order.id}>
                <td>#{order.id}</td>
                <td>{order.coach_id || "-"}</td>
                <td>{order.vendor_name || order.vendor_id || "-"}</td>
                <td>{formatMoney(order.coach_commission_cents)}</td>
                <td>{order.vendor_transfer_id || status.vendor}</td>
                <td>{order.coach_transfer_id || status.coach}</td>
                <td>
                  {stripeUrl ? (
                    <a href={stripeUrl} target="_blank" rel="noreferrer">{order.stripe_payment_intent_id}</a>
                  ) : "-"}
                </td>
                <td>{formatDate(order.paid_at)}</td>
                <td><span className="restringing-pill">{status.label}</span></td>
                {onRetry && (
                  <td>
                    <button
                      type="button"
                      onClick={() => onRetry(order.id)}
                      disabled={retryingOrderId === String(order.id)}
                    >
                      {retryingOrderId === String(order.id) ? "Retrying..." : "Retry"}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default CoachCommissions;
